import { useCallback, useEffect, useMemo, useState } from 'react';
import { POST_MESSAGES_TYPES } from 'modules/codeless-events/consts/iframeParentConstants';
import { setNewConversionsSessionStorage } from 'modules/codeless-events/utils/codelessSessionStorageUtils';
import { CONVERSIONS_TABS } from '../config/conversionTabs';
import { ViewTypes } from '../config/getViewComponents';
import { useConversions } from './useConversions';
import { useCurrentConversion } from './useCurrentConversion';
import { useSyncConversionsWithParent } from './useSyncConversionsWithParent';

export const useTabStepNavigation = tabs => {
    const [activeTabIndex, setActiveTabIndex] = useState(0);
    const [tabSteps, setTabSteps] = useState({});
    const [currentView, setCurrentView] = useState('main');
    const [isNavigateMode, setIsNavigateMode] = useState(false);
    const { touchedConversions, untouchedConversions, conversions } = useConversions();
    const { setFocusedConversionId, existingConversionIds } = useSyncConversionsWithParent(conversions);

    const activeTab = tabs[activeTabIndex];

    const [currentIndex, setCurrentIndex] = useState(() => {
        const activeConversions =
            activeTab?.id === CONVERSIONS_TABS.EVENTS_ON_THIS_PAGE ? touchedConversions : untouchedConversions;
        return activeConversions.length > 0 ? activeConversions.length - 1 : 0;
    });

    const handleSetCurrentIndex = useCallback(
        index => {
            setCurrentIndex(index);
            const conversionId = conversions[index]?.id;
            setFocusedConversionId(conversionId);
        },
        [setFocusedConversionId, conversions]
    );

    const currentConversion = useCurrentConversion(currentIndex);

    const steps = useMemo(
        () => activeTab?.renderSteps({ touchedConversions, untouchedConversions, currentConversion }) || [],
        [activeTab, currentConversion, touchedConversions, untouchedConversions]
    );

    const maxSteps = steps.length;

    const switchTab = tabIndex => {
        setActiveTabIndex(tabIndex);
        const newActiveConversions =
            tabs[tabIndex]?.id === CONVERSIONS_TABS.EVENTS_ON_THIS_PAGE ? touchedConversions : untouchedConversions;
        handleSetCurrentIndex(newActiveConversions.length > 0 ? newActiveConversions.length - 1 : 0);
        setFocusedConversionId(undefined);
    };

    const goNext = useCallback(() => {
        setTabSteps(prevSteps => {
            const currentStep = prevSteps[activeTabIndex] || 0;
            const nextStep = (currentStep + 1) % maxSteps;

            if (nextStep === 0 && currentStep !== 0) {
                setFocusedConversionId(undefined); // Overflow occurred
            }

            return {
                ...prevSteps,
                [activeTabIndex]: nextStep,
            };
        });
    }, [activeTabIndex, maxSteps, setFocusedConversionId]);

    const currentStep = useMemo(() => {
        return tabSteps[activeTabIndex] || 0;
    }, [tabSteps, activeTabIndex]);

    useEffect(() => {
        if (currentStep === 0) {
            setNewConversionsSessionStorage(conversions);
        }
    }, [conversions, currentStep]);

    const goBack = () => {
        if (currentStep > 0) {
            setTabSteps(prevSteps => ({
                ...prevSteps,
                [activeTabIndex]: Math.max((prevSteps[activeTabIndex] || 0) - 1, 0),
            }));
        }
    };

    const resetSteps = () => {
        setTabSteps(prevSteps => ({
            ...prevSteps,
            [activeTabIndex]: 0,
        }));
    };

    const handleFinishSetup = () => {
        setCurrentView(ViewTypes.REVIEW_EVENTS);
        setFocusedConversionId(undefined);
    };

    const handleGoBackToSetup = () => {
        setCurrentView(ViewTypes.MAIN);
        setFocusedConversionId(undefined);
    };

    const handleNavigateMode = () => {
        setIsNavigateMode(prev => {
            const { FINISH_NAVIGATE, NAVIGATE_MODE } = POST_MESSAGES_TYPES;
            window.parent.postMessage({ type: prev ? FINISH_NAVIGATE : NAVIGATE_MODE }, '*');
            return !prev;
        });
    };

    const handleEditClick = useCallback(
        (eventId, tabIndex) => {
            const eventIndex = conversions.findIndex(event => event.id === eventId);

            if (eventIndex !== -1) {
                handleSetCurrentIndex(eventIndex);
                setTabSteps(prevSteps => ({
                    ...prevSteps,
                    [activeTabIndex]: tabIndex,
                }));
            }
        },
        [activeTabIndex, conversions, handleSetCurrentIndex]
    );

    return {
        activeTabIndex,
        activeTab,
        steps,
        currentStep,
        goNext,
        goBack,
        resetSteps,
        switchTab,
        currentView,
        handleGoBackToSetup,
        handleFinishSetup,
        handleSetCurrentIndex,
        currentIndex,
        handleNavigateMode,
        isNavigateMode,
        handleEditClick,
        setFocusedConversionId,
        existingConversionIds,
    };
};
